// @flow
import { get } from 'lodash';

// Bugsnag reports leave the device, so they must not carry credentials or patient data.
// Request headers hold the access-token, bodies hold passwords and patient records,
// query strings hold patient searches and S3 download signatures, and data: URLs
// (fetched to read camera and webcam captures) hold patient photos.

const stripQuery = (url: ?string) => String(url || '').split('?')[0];

const DATA_URL_REQUEST = /^(\S+ data:[^,]*,)[\s\S]*$/;

// A request breadcrumb's "<METHOD> <url>", keeping a data: URL's media type only.
const scrubRequest = (request: string) => (
  DATA_URL_REQUEST.test(request) ? request.replace(DATA_URL_REQUEST, '$1[REDACTED]') : stripQuery(request)
);

// A request's endpoint for grouping in Bugsnag: "/patients/:id/records". Requests to other hosts
// (S3 uploads) keep the host. Ids that went wrong ("undefined", "NaN") stay as they are, since
// those are the bugs worth seeing.
export const routeTemplate = (url: ?string) => stripQuery(url)
  .replace(/^[a-z]+:\/\//i, '')
  .replace(/^[^/]*\/api\/v\d+(?=\/|$)/, '')
  .replace(/\/(\d+|[0-9a-f]{8}-[0-9a-f-]{27,})(?=[/.]|$)/gi, '/:id');

// Uploads are POSTed straight to S3 (records' photos and documents, a patient's photo, the clinic logo)
const S3_URL = /^https?:\/\/[^/?#]+\.amazonaws\.com(:\d+)?([/?#]|$)/i;
const isS3Upload = (method: ?string, url: string) => String(method).toLowerCase() === 'post' && S3_URL.test(url);

// S3 says why it refused an upload in an XML body: <Error><Code>EntityTooSmall</Code><Message>…</Message>
// <ProposedSize>0</ProposedSize><MinSizeAllowed>1</MinSizeAllowed>…</Error>. Only the code (a CamelCase word
// from S3's own list) is kept, plus the sizes in bytes for the size codes: the other fields (Message, Key,
// Condition, ArgumentValue, RequestId, HostId…) can carry the file's name, a patient's data, or the policy.
const S3_CODE = /<Code>\s*([A-Z][A-Za-z0-9]{1,63})\s*<\/Code>/;
const S3_SIZE_CODES = ['EntityTooSmall', 'EntityTooLarge'];
const S3_SIZE_TAGS = ['ProposedSize', 'MinSizeAllowed', 'MaxSizeAllowed'];

// S3's code for refusing an upload, from its response body (a string), or null
export const s3ErrorCode = (body: any): ?string => {
  const code = typeof body === 'string' ? body.match(S3_CODE) : null;
  return code ? code[1] : null;
};

const s3RefusalMetadata = (body: any): Object => {
  const code = s3ErrorCode(body);
  if (!code) return {};
  const metadata: Object = { s3Code: code };
  if (S3_SIZE_CODES.includes(code)) {
    S3_SIZE_TAGS.forEach((tag) => {
      const size = body.match(new RegExp(`<${tag}>\\s*(\\d{1,20})\\s*</${tag}>`));
      if (size) metadata[`s3${tag}`] = Number(size[1]);
    });
  }
  return metadata;
};

// The uploaded file's size in bytes and its media type without parameters (a Blob's type can carry
// ";name=…"), never its name. FormData.prototype.get is missing on old WebViews, which then add nothing.
const uploadedFileMetadata = (body: any): Object => {
  const file = typeof FormData !== 'undefined' && body instanceof FormData && typeof body.get === 'function'
    ? body.get('file') : null;
  if (!file || typeof file.size !== 'number') return {};
  return { fileSize: file.size, fileType: (String(file.type || '').match(/^[\w.+-]{1,50}\/[\w.+-]{1,50}/) || [''])[0] };
};

// Reading an upload's details never throws, so the report and the failure the user sees stay as they were
const safely = (read: () => Object): Object => {
  try {
    return read();
  } catch (e) {
    return {};
  }
};

// What a failed axios request was, without its headers or query string, and without its body, except
// for an upload to S3: why S3 refused it and the file's size and type (never the name).
export const requestMetadata = (error: Object) => {
  const method = get(error, ['config', 'method']);
  const url = stripQuery(get(error, ['config', 'url']));
  const upload = isS3Upload(method, url);
  return {
    method,
    url,
    status: get(error, ['response', 'status']),
    code: get(error, 'code'),
    message: get(error, 'message'),
    ...(upload ? safely(() => s3RefusalMetadata(get(error, ['response', 'data']))) : {}),
    ...(upload ? safely(() => uploadedFileMetadata(get(error, ['config', 'data']))) : {}),
  };
};

// ---------------------------------------------------------------------------------------------------------------------
// The record's rich-text editor (Tiptap on ProseMirror, in a chunk of its own). Its errors can quote the document they
// were given, the patient's record: ProseMirror's "Invalid content for node paragraph: <…>", Tiptap's "[tiptap
// error]: Invalid JSON content" with that one as its cause. An error thrown in the editor's code (from a keystroke, a
// paste: no error boundary sees those) or quoting a document goes to Bugsnag and to the console with its class only.

// The editor chunk's own files, as stack frames name them (production names a chunk by its number: the editor marks
// its file when it loads, markEditorCode), and the editor's packages by their path (the dev server, the tests)
const editorFiles: Set<string> = new Set();
const EDITOR_MODULES = /[\\/]node_modules[\\/](?:@tiptap[\\/]|prosemirror-)/;
// What quotes the content it was given
const QUOTES_CONTENT = /\[tiptap|invalid content for node|invalid (?:json|html) content|invalid element found|passed value/i;

// The file of each frame of a stack: V8's "at f (file:1:2)" and "at file:1:2", JavaScriptCore's and Gecko's
// "f@file:1:2"
const FRAME_FILE = /(?:\(|@|\bat )((?:[a-z][a-z0-9+.-]*:\/\/|\/)[^\s()@]*?):\d+:\d+\)?\s*$/gim;
const frameFiles = (stack: ?string): Array<string> => {
  const files = [];
  const text = String(stack || '');
  FRAME_FILE.lastIndex = 0;
  for (let match = FRAME_FILE.exec(text); match; match = FRAME_FILE.exec(text)) files.push(match[1]);
  return files;
};
const isEditorFile = (file: ?string): boolean => {
  const name = String(file || '');
  return editorFiles.has(name.split(/[?#]/)[0]) || EDITOR_MODULES.test(name);
};

// The editor's chunk, when it loads: `stack` is a stack made in its code (its first frame is that chunk's file)
export const markEditorCode = (stack: ?string) => {
  const [file] = frameFiles(stack);
  if (file) editorFiles.add(file.split(/[?#]/)[0]);
};

export const EDITOR_ERROR_MESSAGE = 'Redacted: an error of the record editor (it may quote the record)';

// The editor's own reports (editorReports' reportedError): its class, the kind's fixed words and the frames, nothing
// of the record. They go as they are, their words readable.
const textFreeReports: WeakSet<Object> = new WeakSet();
export const markTextFreeReport = (error: Object): Object => {
  textFreeReports.add(error);
  return error;
};
const isTextFreeReport = (error: any): boolean => (
  Boolean(error) && typeof error === 'object' && textFreeReports.has(error)
);

// An error of the editor's code, or one that quotes a document
export const isEditorError = (error: any): boolean => {
  if (!error) return false;
  let message = '';
  let stack = '';
  try {
    message = String(error.message || '');
    stack = String(error.stack || '');
  } catch (e) {
    return false;
  }
  return QUOTES_CONTENT.test(message) || frameFiles(stack).some(isEditorFile);
};

// The stack's frames alone: where it failed, without the message the stack starts with (V8 writes it there)
export const stackFrames = (stack: ?string): Array<string> => String(stack || '').split('\n').filter((line) => (
  /^\s*at (?:(?:new|async) )?(?:[^\s()]+ (?:\[as [^\]]+\] )?)?\(?(?:[a-z][a-z0-9+.-]*:\/\/|\/)[^\s()]*:\d+:\d+\)?\s*$/i.test(line)
  || /^(?:[^\s@]+|(?:global|module|eval) code)?@(?:[a-z][a-z0-9+.-]*:\/\/|\/)[^\s()@]*:\d+:\d+\s*$/i.test(line)
));

// A console breadcrumb's text that quotes a document (one the editor's guard didn't see: logged before it loaded)
const scrubLog = (metadata: Object) => {
  Object.keys(metadata).forEach((key) => {
    if (key !== 'severity' && typeof metadata[key] === 'string' && QUOTES_CONTENT.test(metadata[key])) {
      metadata[key] = '[REDACTED]'; // eslint-disable-line no-param-reassign
    }
  });
};

// Bugsnag's onError: an event of the editor's code, or one whose message (or its cause's) quotes a document, goes
// with its class and its frames, every message of it replaced by EDITOR_ERROR_MESSAGE. The editor's own text-free
// reports (markTextFreeReport) go as they are.
export const scrubEditorEvent = (event: Object) => {
  const errors = event.errors || [];
  const fromEditor = !isTextFreeReport(event.originalError) && errors.some((error) => (
    QUOTES_CONTENT.test(String(error.errorMessage || ''))
    || (error.stacktrace || []).some((frame) => isEditorFile(frame.file))
  ));
  if (fromEditor) {
    errors.forEach((error) => {
      error.errorMessage = EDITOR_ERROR_MESSAGE; // eslint-disable-line no-param-reassign
    });
  }
  (event.breadcrumbs || []).forEach((breadcrumb) => {
    if (breadcrumb.type === 'log' && breadcrumb.metadata) scrubLog(breadcrumb.metadata);
  });
};

// Bugsnag's onBreadcrumb: keeps every breadcrumb (returning false would drop it), minus
// URL query strings, data: URL contents, the text of what was clicked (often a patient's name)
// and a console line that quotes a record's document.
export const scrubBreadcrumb = (breadcrumb: Object) => {
  const metadata = breadcrumb.metadata || {};

  if (breadcrumb.type === 'request' && typeof metadata.request === 'string') {
    metadata.request = scrubRequest(metadata.request);
  }

  if (breadcrumb.type === 'user' && metadata.targetText) {
    metadata.targetText = '[REDACTED]';
  }

  if (breadcrumb.type === 'log') scrubLog(metadata);
};
